import { useState } from "react"
import { Link } from "react-router-dom"

/* form */
import Input from "../../../shared/components/form/input"
import Radio from "../../../shared/components/form/Radio"
import Checkbox from "../../../shared/components/form/checkbox"
import Select from 'react-select'
import selectStyles from "../../../shared/css/SelectStyle"
import FormLayout from "../../../shared/components/FormLayout/FormLayout"
import FormSection from "../../../shared/components/FormLayout/FormSection"
import Button from "../../../shared/components/Button/Button"
import UfSelect from "../../../shared/components/UfSelect/UfSelect"
import CitySelect from "../../../shared/components/CitySelect/CitySelect"
import CepSearchTrigger from "../../../shared/components/CepSearchTrigger/CepSearchTrigger"
import CnpjSearchTrigger from "../../../shared/components/CnpjSearchTrigger/CnpjSearchTrigger"
import { normalizeCNPJDigits, maskCNPJ } from "../../../shared/functions/cnpjFormat"
import Modal from "../../../shared/modal/Modal"
import ConfirmDeleteModal from "../../../shared/components/ConfirmDeleteModal/ConfirmDeleteModal"
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa'

/* Hooks */
import useFlashMessage from '../../../hooks/useFlashMessage'

import "../../../shared/css/subItemList.css"

const EMPTY_CONTACT_DRAFT = { name: '', phone: '' }

const IE_INDICATOR_OPTIONS = [
    { selectName: "ieIndicator", value: "contribuinte", label: "Contribuinte de ICMS" },
    { selectName: "ieIndicator", value: "isento", label: "Contribuinte isento de IE" },
    { selectName: "ieIndicator", value: "nao_contribuinte", label: "Não contribuinte" },
]

function FormClient({ handleSubmit, clientData, btnText, btCancelLink, btCancelAction, title }) {

    const { setFlashMessage } = useFlashMessage()
    const [client, setClient] = useState(clientData || { personType: "fisica" })

    const [modalContact, setModalContact] = useState(false)
    const [editingContactIndex, setEditingContactIndex] = useState(null)
    const [contactDraft, setContactDraft] = useState(EMPTY_CONTACT_DRAFT)
    const [modalDeleteContact, setModalDeleteContact] = useState(false)
    const [deleteContactIndex, setDeleteContactIndex] = useState(null)

    const contacts = client.contacts || []

    function openAddContactModal() {
        setContactDraft(EMPTY_CONTACT_DRAFT)
        setEditingContactIndex(null)
        setModalContact(true)
    }

    function openEditContactModal(idx) {
        setContactDraft({ ...contacts[idx] })
        setEditingContactIndex(idx)
        setModalContact(true)
    }

    function closeContactModal() {
        setContactDraft(EMPTY_CONTACT_DRAFT)
        setEditingContactIndex(null)
        setModalContact(false)
    }

    function handleContactDraftChange(e) {
        setContactDraft({ ...contactDraft, [e.target.name]: e.target.value })
    }

    function saveContactDraft(e) {
        e.preventDefault()
        // Evita que o submit borbulhe (via árvore React, mesmo com o Modal via portal) e
        // acione também o onSubmit do formulário de Cliente.
        e.stopPropagation()

        if (!contactDraft.name) {
            setFlashMessage('Digite o nome do contato', 'error')
            return
        }
        if (!contactDraft.phone) {
            setFlashMessage('Digite o telefone do contato', 'error')
            return
        }

        const rows = editingContactIndex !== null
            ? contacts.map((c, i) => (i === editingContactIndex ? contactDraft : c))
            : [...contacts, contactDraft]
        setClient({ ...client, contacts: rows })
        closeContactModal()
    }

    function askRemoveContact(idx) {
        setDeleteContactIndex(idx)
        setModalDeleteContact(true)
    }

    function confirmRemoveContact() {
        setClient({ ...client, contacts: contacts.filter((_, i) => i !== deleteContactIndex) })
        setDeleteContactIndex(null)
        setModalDeleteContact(false)
    }

    function handleChange(e) {
        setClient({ ...client, [e.target.name]: e.target.value })
    }

    // CNPJ é guardado só com dígitos — a máscara é só visual, nunca digitada pelo usuário.
    // Isso evita o problema que já tivemos no bestforce: um cliente digitando com ponto/traço e
    // outro só número, deixando o cadastro inconsistente (duplicado não detectado, etc).
    function handleChangeCnpj(e) {
        const { value } = e.target
        let digits = normalizeCNPJDigits(value)
        // apagou só um caractere da máscara (ponto, barra, hífen): remove o último dígito também,
        // senão o backspace "trava" sem efeito visível
        if (digits === normalizeCNPJDigits(client.CNPJ) && value.length < maskCNPJ(client.CNPJ).length) {
            digits = digits.slice(0, -1)
        }
        setClient({ ...client, CNPJ: digits })
    }
    //Radio form start
    const handleChangeRadio = (name, value) => {
        setClient({ ...client, [name]: value })
    }
    //Radio form end

    function handleChangeCheckbox(name, value) {
        setClient({ ...client, [name]: value })
    }

    function handleSelectChange(selectedOption) {
        // Trocar a UF invalida a cidade escolhida antes (era de outro estado).
        if (selectedOption.selectName === 'state') {
            setClient({ ...client, state: selectedOption.value, city: '' })
            return
        }
        setClient({ ...client, [selectedOption.selectName]: selectedOption.value })
    }

    function handleCepFound(address) {
        setClient((prev) => ({
            ...prev,
            address: address.street || prev.address,
            district: address.district || prev.district,
            city: address.city || prev.city,
            state: address.state || prev.state,
            cityCode: address.cityCode || prev.cityCode,
        }))
    }

    function handleCnpjFound(company) {
        setClient((prev) => ({
            ...prev,
            corporateName: company.corporateName || prev.corporateName,
            fantasyName: company.fantasyName || prev.fantasyName,
            address: company.address || prev.address,
            number: company.number || prev.number,
            complement: company.complement || prev.complement,
            district: company.district || prev.district,
            city: company.city || prev.city,
            state: company.state || prev.state,
            cep: company.cep || prev.cep,
            cityCode: company.cityCode || prev.cityCode,
            email: company.email || prev.email,
        }))
    }

    const selectedIeIndicator = IE_INDICATOR_OPTIONS.find((o) => o.value === client.ieIndicator) || ""

    function submit(e) {
        e.preventDefault()
        if (contacts.length === 0) {
            setFlashMessage('Cadastre ao menos um contato com telefone', 'error')
            return
        }
        handleSubmit(client)
    }

    return (
        <FormLayout
            title={title}
            onSubmit={submit}
            actions={
                <>
                    <Button variant="save" type="submit">{btnText || 'Salvar'}</Button>
                    {btCancelLink
                        ? <Link className="link-default" to={btCancelLink}><Button variant="cancel">Cancelar</Button></Link>
                        : btCancelAction && <Button variant="cancel" onClick={btCancelAction}>Cancelar</Button>
                    }
                </>
            }
        >
            <FormSection label="Identificação">
                <div className="fl-row">
                    <div className="fl-col-wide form-group">
                        <Input
                            text="Cliente"
                            type="text"
                            name="name"
                            placeholder="digite o nome do fornecedor"
                            handleOnChange={handleChange}
                            value={client.name || ''}
                        />
                    </div>
                </div>
                <div className="form-group">
                    <Checkbox
                        text="Cliente desabilitado"
                        name="disabled"
                        handleOnChange={() => handleChangeCheckbox("disabled", !client.disabled)}
                        check={client.disabled || false}
                    />
                </div>
                <div className="fl-row">
                    <div className="fl-col-mid form-group">
                        <Radio
                            options={[
                                { value: 'fisica', label: "Pessoa física" },
                                { value: 'juridica', label: "Pessoa jurídica" }
                            ]}
                            name={"personType"}
                            value={client.personType}
                            defaultValue="fisica"
                            handleOnChange={handleChangeRadio}
                        />
                    </div>
                </div>
            </FormSection>

            {client.personType === "juridica" &&
                <FormSection label="Pessoa Jurídica">
                    <div className="fl-row">
                        <div className="fl-col-wide form-group">
                            <Input
                                text="Razão Social"
                                type="text"
                                name="corporateName"
                                placeholder="digite a razão social"
                                handleOnChange={handleChange}
                                value={client.corporateName || ''}
                            />
                        </div>
                        <div className="fl-col-mid form-group">
                            <Input
                                text="Nome Fantasia"
                                type="text"
                                name="fantasyName"
                                placeholder=""
                                handleOnChange={handleChange}
                                value={client.fantasyName || ''}
                            />
                        </div>
                        <div className="fl-col-narrow form-group">
                            <label className="input-label">CNPJ</label>
                            <CnpjSearchTrigger name="CNPJ" value={maskCNPJ(client.CNPJ)} handleOnChange={handleChangeCnpj} onFound={handleCnpjFound} />
                        </div>
                    </div>
                </FormSection>
            }
            {client.personType === "fisica" &&
                <FormSection label="Pessoa Física">
                    <div className="fl-row">
                        <div className="fl-col-wide form-group">
                            <Input
                                text="Nome Completo"
                                type="text"
                                name="completeName"
                                placeholder="digite seu nome completo"
                                handleOnChange={handleChange}
                                value={client.completeName || ''}
                            />
                        </div>
                        <div className="fl-col-narrow form-group">
                            <Input
                                text="CPF"
                                type="number"
                                name="CPF"
                                placeholder=""
                                handleOnChange={handleChange}
                                value={client.CPF || ''}
                            />
                        </div>
                    </div>
                </FormSection>
            }

            <FormSection label="Endereço e Contato">
                <div className="fl-row">
                    <div className="fl-col-narrow form-group">
                        <label className="input-label">CEP</label>
                        <CepSearchTrigger name="cep" value={client.cep} handleOnChange={handleChange} onFound={handleCepFound} />
                    </div>
                    <div className="fl-col-wide form-group">
                        <Input
                            text="Endereço"
                            type="text"
                            name="address"
                            placeholder=""
                            handleOnChange={handleChange}
                            value={client.address || ''}
                        />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input
                            text="Bairro"
                            type="text"
                            name="district"
                            placeholder=""
                            handleOnChange={handleChange}
                            value={client.district || ''}
                        />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <Input
                            text="Número"
                            type="number"
                            name="number"
                            placeholder=""
                            handleOnChange={handleChange}
                            value={client.number || ''}
                        />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <Input
                            text="Complemento"
                            type="text"
                            name="complement"
                            placeholder=""
                            handleOnChange={handleChange}
                            value={client.complement || ''}
                        />
                    </div>
                </div>
                <div className="fl-row">
                    <div className="fl-col-narrow form-group">
                        <label className="input-label">UF</label>
                        <UfSelect value={client.state} onChange={handleSelectChange} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <label className="input-label">Cidade</label>
                        <CitySelect uf={client.state} value={client.city} onChange={handleSelectChange} />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <Input
                            text="Cód. IBGE do Município"
                            type="text"
                            name="cityCode"
                            placeholder="opcional"
                            handleOnChange={handleChange}
                            value={client.cityCode || ''}
                        />
                    </div>
                </div>
                <div className="fl-row">
                    <div className="fl-col-wide form-group">
                        <Input
                            text="Email"
                            type="email"
                            name="email"
                            placeholder=""
                            handleOnChange={handleChange}
                            value={client.email || ''}
                        />
                    </div>
                </div>

                <div className="fl-row">
                    <div className="fl-col-wide form-group">
                        <label className="input-label">
                            Contatos
                            <div onClick={openAddContactModal} title="Adicionar contato">
                                <div className="plus-sinal">
                                    <div className="plus-sinal-tip"></div>
                                    <div className="plus-sinal-middle"></div>
                                    <div className="plus-sinal-tip"></div>
                                </div>
                            </div>
                        </label>
                        <span className="fl-section-hint">Nome e telefone de cada pessoa de contato desse cliente — cadastre ao menos um.</span>
                    </div>
                </div>

                <div className="sil-list">
                    {contacts.length === 0 && (
                        <span className="sil-empty">Nenhum contato cadastrado ainda — clique no "+" acima pra adicionar.</span>
                    )}
                    {contacts.map((row, idx) => (
                        <div key={idx} className="sil-row" title="Clique para editar" onClick={() => openEditContactModal(idx)}>
                            <span className="sil-label">{row.name}</span>
                            <span className="sil-value">{row.phone}</span>
                            <FaPencilAlt className="shd-icon-row-edit" title="Editar" onClick={(e) => { e.stopPropagation(); openEditContactModal(idx) }} />
                            <FaTrashAlt className="shd-icon-row-trash" title="Remover" onClick={(e) => { e.stopPropagation(); askRemoveContact(idx) }} />
                        </div>
                    ))}
                </div>

                <Modal
                    modalStatus={modalContact}
                    changeStatus={setModalContact}
                    w={45}
                    content={
                        <FormLayout
                            title={editingContactIndex !== null ? "Editar Contato" : "Adicionar Contato"}
                            onSubmit={saveContactDraft}
                            actions={
                                <>
                                    <Button variant="finish" type="submit">{editingContactIndex !== null ? "Salvar" : "Adicionar"}</Button>
                                    <Button variant="cancel" type="button" onClick={closeContactModal}>Cancelar</Button>
                                </>
                            }
                        >
                            <div className="fl-row">
                                <div className="fl-col-wide form-group">
                                    <Input
                                        text="Nome"
                                        type="text"
                                        name="name"
                                        placeholder="nome do contato"
                                        handleOnChange={handleContactDraftChange}
                                        value={contactDraft.name || ''}
                                    />
                                </div>
                                <div className="fl-col-mid form-group">
                                    <Input
                                        text="Telefone"
                                        type="tel"
                                        name="phone"
                                        placeholder=""
                                        handleOnChange={handleContactDraftChange}
                                        value={contactDraft.phone || ''}
                                    />
                                </div>
                            </div>
                        </FormLayout>
                    }
                />

                <ConfirmDeleteModal
                    modalStatus={modalDeleteContact}
                    changeStatus={setModalDeleteContact}
                    onConfirm={confirmRemoveContact}
                    message="Deseja realmente excluir esse contato?"
                    itemName={deleteContactIndex !== null ? (contacts[deleteContactIndex] || {}).name : ''}
                />

                <div className="fl-row">
                    <div className="fl-col-wide form-group">
                        <Input
                            text="OBS:"
                            type="text"
                            name="obs"
                            placeholder=""
                            handleOnChange={handleChange}
                            value={client.obs || ''}
                        />
                    </div>
                </div>
            </FormSection>

            <FormSection label="Dados Fiscais">
                <div className="fl-row">
                    <div className="fl-col-mid form-group">
                        <Input
                            text="Inscrição Estadual"
                            type="text"
                            name="stateRegistration"
                            placeholder="obrigatório se contribuinte de ICMS"
                            handleOnChange={handleChange}
                            value={client.stateRegistration || ''}
                        />
                    </div>
                    <div className="fl-col-mid form-group">
                        <label className="input-label">Indicador de IE:</label>
                        <Select
                            options={IE_INDICATOR_OPTIONS}
                            styles={selectStyles}
                            onChange={handleSelectChange}
                            value={selectedIeIndicator}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                </div>
            </FormSection>
        </FormLayout>
    )
}

export default FormClient
